import { DataService } from '../services/data.js';
import { getIcon, showToast } from '../components/ui.js';

let cachedResources = [];
let currentFilter = 'all'; // 'all' | 'images' | 'documents'
let searchTerm = '';

export async function renderResourcesView() {
  try {
    cachedResources = await DataService.listResources();
  } catch (err) {
    console.error('Error al cargar recursos:', err);
    cachedResources = [];
  }

  const totalFiles = cachedResources.length;
  const imageFiles = cachedResources.filter(r => isImage(r.name));
  const pdfFiles = cachedResources.filter(r => isPdf(r.name));
  const totalSizeBytes = cachedResources.reduce((acc, r) => acc + (r.size || 0), 0);
  const formattedStorage = formatBytes(totalSizeBytes);

  return `
    <div class="space-y-6 animate-fade-in">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-5">
        <div>
          <div class="flex items-center gap-2">
            <span class="p-1.5 rounded bg-surface-subtle border border-border text-text-primary">
              ${getIcon('folder', 'w-4 h-4')}
            </span>
            <h1 class="text-lg font-bold text-text-primary">Biblioteca de Recursos & Archivos</h1>
          </div>
          <p class="text-xs text-text-secondary mt-1">
            Almacenamiento público en Supabase Storage (PNG, JPG, PDF) para logos, banners y recursos en plantillas de email.
          </p>
        </div>

        <div class="flex items-center gap-2">
          <button id="btn-refresh-resources" class="px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-surface hover:bg-surface-subtle text-text-primary flex items-center gap-1.5 shadow-sm transition-all cursor-pointer">
            ${getIcon('refreshCw', 'w-3.5 h-3.5 text-text-secondary')}
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      <!-- Quick Metrics Bar -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Total Archivos</p>
          <p class="text-xl font-bold text-text-primary font-mono mt-1">${totalFiles}</p>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Imágenes (PNG / JPG)</p>
          <p class="text-xl font-bold text-blue-600 font-mono mt-1">${imageFiles.length}</p>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Documentos PDF</p>
          <p class="text-xl font-bold text-purple-600 font-mono mt-1">${pdfFiles.length}</p>
        </div>
        <div class="bg-surface p-4 rounded-md border border-border shadow-sm">
          <p class="text-[11px] font-medium text-text-secondary">Espacio Ocupado</p>
          <p class="text-xl font-bold text-zinc-700 font-mono mt-1">${formattedStorage}</p>
        </div>
      </div>

      <!-- Drag & Drop Uploader Panel -->
      <div class="bg-surface rounded-md border border-border p-5 space-y-4 shadow-sm">
        <div class="flex items-center justify-between border-b border-border pb-3">
          <div>
            <h3 class="text-xs font-bold uppercase tracking-wider text-text-primary">Subir Nuevo Recurso</h3>
            <p class="text-[11px] text-text-secondary">Arrastra archivos o selecciónalos desde tu ordenador</p>
          </div>
          <span class="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
            Storage Activo • Bucket Público
          </span>
        </div>

        <div 
          id="dropzone-area" 
          class="border-2 border-dashed border-border hover:border-zinc-400 bg-surface-subtle/50 rounded-lg p-6 sm:p-8 text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-3"
        >
          <input 
            type="file" 
            id="file-input-resource" 
            accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" 
            multiple 
            class="hidden" 
          />
          <div class="w-12 h-12 rounded-full bg-surface border border-border flex items-center justify-center text-text-secondary shadow-sm">
            ${getIcon('upload', 'w-6 h-6 text-primary')}
          </div>
          <div class="space-y-1">
            <p class="text-xs font-semibold text-text-primary">
              Haz clic aquí para seleccionar archivos o arrástralos a esta zona
            </p>
            <p class="text-[11px] text-text-secondary">
              Formatos soportados: <strong>PNG, JPG, WebP, SVG, PDF</strong> (Máx. 10 MB por archivo)
            </p>
          </div>
          <div id="upload-status-indicator" class="hidden text-xs text-primary font-medium flex items-center gap-2">
            <span class="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
            <span id="upload-status-text">Subiendo archivos...</span>
          </div>
        </div>
      </div>

      <!-- Filter & Search Toolbar -->
      <div class="bg-surface rounded-md border border-border p-4 space-y-4 shadow-sm">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <!-- Filter Tabs -->
          <div class="flex items-center gap-1.5">
            <button class="btn-res-filter px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${currentFilter === 'all' ? 'bg-primary text-white font-semibold shadow-xs' : 'bg-surface-subtle text-text-secondary hover:text-text-primary border border-border'}" data-filter="all">
              Todos (${totalFiles})
            </button>
            <button class="btn-res-filter px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${currentFilter === 'images' ? 'bg-primary text-white font-semibold shadow-xs' : 'bg-surface-subtle text-text-secondary hover:text-text-primary border border-border'}" data-filter="images">
              Imágenes (${imageFiles.length})
            </button>
            <button class="btn-res-filter px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${currentFilter === 'documents' ? 'bg-primary text-white font-semibold shadow-xs' : 'bg-surface-subtle text-text-secondary hover:text-text-primary border border-border'}" data-filter="documents">
              PDFs (${pdfFiles.length})
            </button>
          </div>

          <!-- Search Input -->
          <div class="relative w-full sm:w-64">
            <span class="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-text-secondary">
              ${getIcon('search', 'w-3.5 h-3.5')}
            </span>
            <input 
              type="text" 
              id="input-search-resources" 
              value="${searchTerm}" 
              placeholder="Buscar por nombre..." 
              class="w-full pl-8 pr-3 py-1.5 text-xs bg-surface-subtle border border-border rounded focus:bg-surface outline-none"
            />
          </div>
        </div>

        <!-- Resources Grid -->
        <div id="resources-grid-container">
          ${renderResourcesGrid()}
        </div>
      </div>
    </div>

    <!-- Modal for Resource Preview & Embed Code -->
    <div id="resource-modal-container"></div>
  `;
}

function renderResourcesGrid() {
  let list = [...cachedResources];

  if (currentFilter === 'images') {
    list = list.filter(r => isImage(r.name));
  } else if (currentFilter === 'documents') {
    list = list.filter(r => isPdf(r.name));
  }

  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase().trim();
    list = list.filter(r => r.name.toLowerCase().includes(term));
  }

  if (list.length === 0) {
    return `
      <div class="py-12 text-center space-y-3 bg-surface-subtle/30 rounded border border-dashed border-border/80">
        <div class="w-10 h-10 rounded-full bg-surface border border-border flex items-center justify-center mx-auto text-text-secondary">
          ${getIcon('folder', 'w-5 h-5')}
        </div>
        <p class="text-xs font-semibold text-text-primary">No se encontraron recursos</p>
        <p class="text-[11px] text-text-secondary max-w-sm mx-auto">
          ${searchTerm ? 'No hay archivos que coincidan con tu búsqueda.' : 'Aún no has subido imágenes o documentos. Usa la zona superior para subir tus primeros archivos.'}
        </p>
      </div>
    `;
  }

  return `
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-1">
      ${list.map(r => {
        const isImg = isImage(r.name);
        const isDoc = isPdf(r.name);
        const sizeFormatted = formatBytes(r.size);
        const dateFormatted = r.created_at ? new Date(r.created_at).toLocaleDateString('es-AR') : '-';

        return `
          <div class="bg-surface rounded-lg border border-border hover:border-zinc-400 p-3 flex flex-col justify-between transition-all shadow-sm group">
            <div>
              <!-- Thumbnail / Preview Area (Safely Framed) -->
              <div class="res-card-preview-trigger w-full h-40 bg-zinc-100 rounded-md border border-border/70 overflow-hidden flex items-center justify-center relative mb-2.5 cursor-pointer" data-name="${r.name}">
                ${isImg ? `
                  <img 
                    src="${r.publicUrl}" 
                    alt="${r.name}" 
                    loading="lazy" 
                    class="res-img-element w-full h-full object-contain p-2 transition-transform duration-200 group-hover:scale-105" 
                  />
                  <div class="res-error-placeholder hidden p-4 text-text-secondary flex flex-col items-center justify-center gap-1.5 text-center">
                    ${getIcon('image', 'w-7 h-7 text-text-secondary/50')}
                    <span class="text-[10px] font-mono text-text-secondary">Vista previa no disponible</span>
                  </div>
                ` : isDoc ? `
                  <div class="flex flex-col items-center justify-center gap-1.5 p-4 text-center">
                    <span class="p-2.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200">
                      ${getIcon('fileText', 'w-7 h-7')}
                    </span>
                    <span class="text-[10px] font-mono uppercase font-bold text-purple-700">Documento PDF</span>
                  </div>
                ` : `
                  <div class="flex flex-col items-center justify-center gap-1 text-text-secondary">
                    ${getIcon('fileText', 'w-7 h-7')}
                    <span class="text-[10px] font-mono">Archivo</span>
                  </div>
                `}

                <!-- Badge overlay -->
                <span class="absolute top-2 right-2 text-[9px] font-mono font-semibold px-2 py-0.5 rounded bg-zinc-900/80 text-white pointer-events-none z-10 shadow-xs">
                  ${isImg ? 'IMG' : isDoc ? 'PDF' : 'FILE'}
                </span>
              </div>

              <!-- Name & Metadata -->
              <div class="space-y-1">
                <p class="text-xs font-bold text-text-primary truncate" title="${r.name}">
                  ${r.name}
                </p>
                <div class="flex items-center justify-between text-[10px] font-mono text-text-secondary">
                  <span>${sizeFormatted}</span>
                  <span>${dateFormatted}</span>
                </div>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="pt-3 border-t border-border/60 mt-3 space-y-1.5">
              <div class="grid grid-cols-2 gap-1.5">
                <button 
                  type="button" 
                  class="btn-copy-resource-url px-2 py-1.5 text-[11px] font-medium rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary flex items-center justify-center gap-1 shadow-xs transition-colors cursor-pointer"
                  data-url="${r.publicUrl}" 
                  title="Copiar link directo para insertar en plantillas"
                >
                  ${getIcon('copy', 'w-3 h-3 text-text-secondary')}
                  <span>Copiar Link</span>
                </button>

                ${isImg ? `
                  <button 
                    type="button" 
                    class="btn-copy-img-tag px-2 py-1.5 text-[11px] font-medium rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary flex items-center justify-center gap-1 shadow-xs transition-colors cursor-pointer"
                    data-url="${r.publicUrl}" 
                    data-name="${r.name}" 
                    title="Copiar etiqueta HTML <img> lista para pegar"
                  >
                    ${getIcon('image', 'w-3 h-3 text-blue-600')}
                    <span>Copiar &lt;img&gt;</span>
                  </button>
                ` : `
                  <a 
                    href="${r.publicUrl}" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    class="px-2 py-1.5 text-[11px] font-medium rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary flex items-center justify-center gap-1 shadow-xs transition-colors text-center"
                    title="Abrir en pestaña nueva"
                  >
                    ${getIcon('externalLink', 'w-3 h-3 text-text-secondary')}
                    <span>Ver PDF</span>
                  </a>
                `}
              </div>

              <div class="flex items-center justify-between pt-1">
                <button 
                  type="button" 
                  class="btn-open-detail-modal text-[10px] text-text-secondary hover:text-primary underline flex items-center gap-1 cursor-pointer"
                  data-name="${r.name}"
                >
                  ${getIcon('eye', 'w-3 h-3')}
                  <span>Detalle / Zoom</span>
                </button>

                <button 
                  type="button" 
                  class="btn-delete-resource text-text-secondary hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors cursor-pointer"
                  data-name="${r.name}" 
                  title="Eliminar recurso del Storage"
                >
                  ${getIcon('trash', 'w-3.5 h-3.5')}
                </button>
              </div>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

export function initResourcesEvents() {
  const dropzone = document.getElementById('dropzone-area');
  const fileInput = document.getElementById('file-input-resource');
  const searchInput = document.getElementById('input-search-resources');
  const btnRefresh = document.getElementById('btn-refresh-resources');

  // Trigger file selection on click
  if (dropzone && fileInput) {
    dropzone.addEventListener('click', () => {
      fileInput.click();
    });

    // Drag and drop events
    ['dragenter', 'dragover'].forEach(eventName => {
      dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.add('border-primary', 'bg-surface-subtle');
      });
    });

    ['dragleave', 'drop'].forEach(eventName => {
      dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
        dropzone.classList.remove('border-primary', 'bg-surface-subtle');
      });
    });

    dropzone.addEventListener('drop', (e) => {
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFilesUpload(Array.from(files));
      }
    });

    fileInput.addEventListener('change', (e) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFilesUpload(Array.from(files));
      }
    });
  }

  // Search input
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      searchTerm = e.target.value;
      refreshGrid();
    });
  }

  // Refresh button
  if (btnRefresh) {
    btnRefresh.addEventListener('click', async () => {
      btnRefresh.disabled = true;
      try {
        cachedResources = await DataService.listResources();
        refreshGrid();
        showToast('Lista de recursos sincronizada.');
      } catch (err) {
        showToast(`Error al actualizar: ${err.message}`, 'error');
      } finally {
        btnRefresh.disabled = false;
      }
    });
  }

  // Filter tabs
  document.querySelectorAll('.btn-res-filter').forEach(btn => {
    btn.addEventListener('click', (e) => {
      currentFilter = e.currentTarget.dataset.filter;
      document.querySelectorAll('.btn-res-filter').forEach(b => {
        b.className = 'btn-res-filter px-3 py-1.5 rounded-md text-xs font-medium transition-colors bg-surface-subtle text-text-secondary hover:text-text-primary border border-border';
      });
      e.currentTarget.className = 'btn-res-filter px-3 py-1.5 rounded-md text-xs font-medium transition-colors bg-primary text-white font-semibold shadow-xs';
      refreshGrid();
    });
  });

  bindGridItemEvents();
}

function bindGridItemEvents() {
  // Safe Image error handler
  document.querySelectorAll('.res-img-element').forEach(img => {
    img.addEventListener('error', () => {
      img.classList.add('hidden');
      const placeholder = img.parentElement?.querySelector('.res-error-placeholder');
      if (placeholder) placeholder.classList.remove('hidden');
    });
  });

  // Copy URL button
  document.querySelectorAll('.btn-copy-resource-url').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const url = btn.dataset.url;
      if (url) {
        navigator.clipboard.writeText(url).then(() => {
          showToast('¡Link público copiado al portapapeles!', 'success');
        }).catch(() => {
          copyFallback(url, 'Link copiado.');
        });
      }
    };
  });

  // Copy HTML img tag
  document.querySelectorAll('.btn-copy-img-tag').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const url = btn.dataset.url;
      const name = btn.dataset.name || 'Imagen';
      const imgTag = `<img src="${url}" alt="${name}" style="max-width: 100%; height: auto; display: block; margin: 16px 0;" />`;
      navigator.clipboard.writeText(imgTag).then(() => {
        showToast('¡Etiqueta <img> HTML copiada! Lista para pegar en la plantilla.', 'success');
      }).catch(() => {
        copyFallback(imgTag, 'Etiqueta HTML copiada.');
      });
    };
  });

  // Delete resource
  document.querySelectorAll('.btn-delete-resource').forEach(btn => {
    btn.onclick = async (e) => {
      e.stopPropagation();
      const name = btn.dataset.name;
      if (!name) return;

      if (confirm(`¿Estás seguro de que deseas eliminar permanentemente el archivo "${name}" de Supabase Storage?`)) {
        try {
          await DataService.deleteResource(name);
          cachedResources = cachedResources.filter(r => r.name !== name);
          refreshGrid();
          showToast('Archivo eliminado con éxito.', 'success');
        } catch (err) {
          showToast(`Error al eliminar archivo: ${err.message}`, 'error');
        }
      }
    };
  });

  // Open detail / zoom modal
  document.querySelectorAll('.res-card-preview-trigger, .btn-open-detail-modal').forEach(el => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const name = el.dataset.name;
      const resource = cachedResources.find(r => r.name === name);
      if (resource) {
        openResourceDetailModal(resource);
      }
    });
  });
}

function openResourceDetailModal(r) {
  const modalContainer = document.getElementById('resource-modal-container') || document.body;
  const isImg = isImage(r.name);
  const isDoc = isPdf(r.name);
  const sizeFormatted = formatBytes(r.size);
  const dateFormatted = r.created_at ? new Date(r.created_at).toLocaleDateString('es-AR') : '-';
  const imgSnippet = `<img src="${r.publicUrl}" alt="${r.name}" style="max-width: 100%; height: auto; display: block; margin: 16px 0;" />`;
  const pdfSnippet = `<p><a href="${r.publicUrl}" target="_blank" style="color: #18181b; font-weight: 600; text-decoration: underline;">Descargar ${r.name} (PDF)</a></p>`;

  const modalEl = document.createElement('div');
  modalEl.id = 'resource-detail-modal';
  modalEl.className = 'fixed inset-0 bg-black/70 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-5 animate-fade-in';
  modalEl.innerHTML = `
    <div class="bg-surface border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden text-xs">
      <!-- Header -->
      <div class="p-3 sm:p-4 border-b border-border flex items-center justify-between bg-surface-subtle/60 shrink-0">
        <div class="flex items-center gap-2 truncate">
          <span class="p-1 rounded bg-surface border border-border text-primary shrink-0">
            ${isImg ? getIcon('image', 'w-4 h-4 text-blue-600') : getIcon('fileText', 'w-4 h-4 text-purple-600')}
          </span>
          <div class="truncate">
            <h3 class="font-bold text-sm text-text-primary truncate">${r.name}</h3>
            <p class="text-[10px] text-text-secondary">${sizeFormatted} • Subido el ${dateFormatted}</p>
          </div>
        </div>
        <button id="btn-close-detail-modal" class="p-1.5 text-text-secondary hover:text-text-primary rounded cursor-pointer">
          ${getIcon('x', 'w-4 h-4')}
        </button>
      </div>

      <!-- Preview Image / Content -->
      <div class="p-4 sm:p-6 bg-zinc-100 flex items-center justify-center overflow-y-auto max-h-[50vh]">
        ${isImg ? `
          <img src="${r.publicUrl}" alt="${r.name}" class="max-h-[45vh] max-w-full object-contain rounded border border-border/80 shadow-xs bg-white p-1" />
        ` : isDoc ? `
          <div class="p-8 bg-white rounded border border-border text-center space-y-2 shadow-xs">
            <span class="p-3 rounded-xl bg-purple-50 text-purple-700 inline-block">
              ${getIcon('fileText', 'w-10 h-10')}
            </span>
            <p class="font-bold text-sm text-text-primary">${r.name}</p>
            <p class="text-text-secondary text-xs">Documento PDF listo para descarga pública</p>
          </div>
        ` : `
          <p class="text-text-secondary">Archivo general</p>
        `}
      </div>

      <!-- Links & Copy inputs -->
      <div class="p-4 space-y-3 bg-surface shrink-0">
        <div>
          <label class="block font-semibold text-text-primary text-[11px] mb-1">Enlace Público Directo (HTTPS)</label>
          <div class="flex gap-2">
            <input type="text" readonly value="${r.publicUrl}" class="flex-1 p-2 bg-surface-subtle border border-border rounded font-mono text-xs select-all outline-none" />
            <button id="btn-modal-copy-url" class="px-3 py-1.5 rounded bg-primary text-white font-semibold text-xs hover:bg-primary-hover shrink-0 flex items-center gap-1 shadow-xs cursor-pointer">
              ${getIcon('copy', 'w-3 h-3')}
              <span>Copiar URL</span>
            </button>
          </div>
        </div>

        <div>
          <label class="block font-semibold text-text-primary text-[11px] mb-1">Código HTML Listo para Plantillas de Email</label>
          <div class="flex gap-2">
            <input type="text" readonly value="${isImg ? imgSnippet.replace(/"/g, '&quot;') : pdfSnippet.replace(/"/g, '&quot;')}" class="flex-1 p-2 bg-surface-subtle border border-border rounded font-mono text-[11px] select-all outline-none" />
            <button id="btn-modal-copy-html" class="px-3 py-1.5 rounded bg-surface border border-border hover:bg-surface-subtle font-semibold text-xs shrink-0 flex items-center gap-1 text-text-primary shadow-xs cursor-pointer">
              ${getIcon('copy', 'w-3 h-3')}
              <span>Copiar HTML</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="p-3 border-t border-border flex items-center justify-between bg-surface-subtle/50 shrink-0">
        <a href="${r.publicUrl}" target="_blank" rel="noopener noreferrer" class="px-3 py-1.5 rounded border border-border bg-surface hover:bg-surface-subtle text-text-primary font-medium flex items-center gap-1.5">
          ${getIcon('externalLink', 'w-3.5 h-3.5')}
          <span>Abrir Original</span>
        </a>

        <div class="flex items-center gap-2">
          <button id="btn-modal-delete-file" class="px-3 py-1.5 rounded text-red-600 hover:bg-red-50 font-medium transition-colors cursor-pointer">
            Eliminar Recurso
          </button>
          <button id="btn-modal-close" class="px-3 py-1.5 rounded bg-surface border border-border hover:bg-surface-subtle text-text-primary font-medium cursor-pointer">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  `;

  modalContainer.appendChild(modalEl);

  const close = () => modalEl.remove();
  document.getElementById('btn-close-detail-modal')?.addEventListener('click', close);
  document.getElementById('btn-modal-close')?.addEventListener('click', close);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) close();
  });

  document.getElementById('btn-modal-copy-url')?.addEventListener('click', () => {
    navigator.clipboard.writeText(r.publicUrl).then(() => {
      showToast('¡URL pública copiada con éxito!', 'success');
    }).catch(() => copyFallback(r.publicUrl, 'URL copiada'));
  });

  document.getElementById('btn-modal-copy-html')?.addEventListener('click', () => {
    const snippet = isImg ? imgSnippet : pdfSnippet;
    navigator.clipboard.writeText(snippet).then(() => {
      showToast('¡Código HTML copiado! Listo para pegar en el editor de email.', 'success');
    }).catch(() => copyFallback(snippet, 'Código HTML copiado'));
  });

  document.getElementById('btn-modal-delete-file')?.addEventListener('click', async () => {
    if (confirm(`¿Eliminar permanentemente "${r.name}" de Supabase Storage?`)) {
      try {
        await DataService.deleteResource(r.name);
        cachedResources = cachedResources.filter(item => item.name !== r.name);
        refreshGrid();
        showToast('Archivo eliminado correctamente.', 'success');
        close();
      } catch (err) {
        showToast(`Error al eliminar: ${err.message}`, 'error');
      }
    }
  });
}

async function handleFilesUpload(files) {
  const statusIndicator = document.getElementById('upload-status-indicator');
  const statusText = document.getElementById('upload-status-text');
  if (statusIndicator) statusIndicator.classList.remove('hidden');

  let successCount = 0;
  let errorCount = 0;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (statusText) statusText.innerText = `Subiendo ${i + 1} de ${files.length}: ${file.name}...`;

    try {
      await DataService.uploadResource(file);
      successCount++;
    } catch (err) {
      console.error(`Error subiendo ${file.name}:`, err);
      errorCount++;
    }
  }

  if (statusIndicator) statusIndicator.classList.add('hidden');

  if (successCount > 0) {
    try {
      // Refresh real state directly from Supabase Storage
      cachedResources = await DataService.listResources();
    } catch (_) {}
    showToast(`¡${successCount} recurso(s) subido(s) con éxito!`, 'success');
    refreshGrid();
  }
  if (errorCount > 0) {
    showToast(`Ocurrió un error al subir ${errorCount} archivo(s).`, 'error');
  }

  // Reset file input
  const fileInput = document.getElementById('file-input-resource');
  if (fileInput) fileInput.value = '';
}

function refreshGrid() {
  const container = document.getElementById('resources-grid-container');
  if (container) {
    container.innerHTML = renderResourcesGrid();
    bindGridItemEvents();
  }
}

function isImage(fileName = '') {
  return /\.(png|jpe?g|webp|svg|gif)$/i.test(fileName);
}

function isPdf(fileName = '') {
  return /\.pdf$/i.test(fileName);
}

function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function copyFallback(text, successMsg) {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
    showToast(successMsg, 'success');
  } catch (err) {
    showToast('No se pudo copiar automáticamente al portapapeles.', 'error');
  }
  document.body.removeChild(textArea);
}
